import React, { useEffect, useState } from 'react';
import { useLoadingScreen } from '../../../../hooks/useLoadingScreen';
import {
  ButtonPrimary,
  Checkbox,
  Input,
} from '@skf/bt-wind-rail-commonlibrary';
import { getEmptyUser, User } from '../../../../store/users/types';
import { useTranslation } from 'react-i18next';
import {
  clearUsersError,
  fetchUsers,
  postUsers,
  getUserInIdentityManager,
} from '../../../../store/users/actions';
import { useDispatch, useSelector } from 'react-redux';
import { IUsersState } from '../../../../store/users/store';
import { StoreState } from '../../../../store/store';
import style from './UserManagementAdd.module.scss';
import { useToast } from '../../../../hooks/useToast';
import { isValidEmail } from '../../../../services/emailService';
import { usePathname } from '../../../../hooks/usePathname';
import {
  UserManagementAssignRolesRoute,
  UserManagementRoute,
} from '../../../../routes/routes';
import UserManagementAddDataTable from './dataTable/UserManagementAddDataTable';
import { useAssignUserRoles } from '../../../../hooks/useAssignUserRoles';
import {
  isConflictResponse,
  isValidResponse,
} from '../../../../services/responseService';
import {
  isAlphaNumeric,
  isAlphaNumericPlus,
} from '../../../../services/stringServices';
import { UsersActionTypes } from '../../../../store/users/actionTypes';

interface IUserManagementAddProps {}

const UserManagementAdd: React.FunctionComponent<IUserManagementAddProps> = () => {
  const { setLoading } = useLoadingScreen();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { addToast } = useToast();
  const { setPathname } = usePathname();
  const { setAssignUserRoles } = useAssignUserRoles();

  const { users, status, message, userIdentityInfo }: IUsersState = useSelector<
    StoreState,
    IUsersState
  >((state) => state.users);

  const [usersToAdd, setUsersToAdd] = useState<User[]>([]);
  const [formUsers, setFormUsers] = useState<User>(getEmptyUser());
  const [isFetching, setFetching] = useState<boolean>(false);
  const [isCheckingIdentity, setIsCheckingIdentity] = useState<boolean>(false);

  // Load the users if not loaded because we need to check if the email/username is already used.
  useEffect(() => {
    if (users === undefined) {
      dispatch(fetchUsers());
      return;
    }
    setLoading(false);
  }, [users]);

  useEffect(() => {
    setAssignUserRoles(usersToAdd);
  }, [usersToAdd]);

  useEffect(() => {
    const _isValidResponse = isValidResponse(status);
    const _isConflict = isConflictResponse(status);
    if (isFetching && _isConflict) {
      addToast({
        status: 'warning',
        message: t('Users_duplicated'),
      });
      dispatch(clearUsersError());
      return setPathname(UserManagementAssignRolesRoute.path);
    }

    if (isFetching && _isValidResponse) {
      addToast({
        status: 'ok',
        message: t('Saved_Users'),
      });
      dispatch(clearUsersError());
      return setPathname(UserManagementAssignRolesRoute.path);
    }

    if (!status || !message) return;
    addToast({
      status: 'danger',
      message: message || `Error ${status}`,
    });
    dispatch(clearUsersError());
    setLoading(false);
    setFetching(false);
  }, [status, message, isFetching]);

  const onDeleteUser = (user: User) => {
    setUsersToAdd(
      usersToAdd.filter((_user) => _user.userEmail !== user.userEmail)
    );
  };

  // Helper function to add user to the list
  const addUserToList = (userToAdd: User) => {
    setUsersToAdd([userToAdd, ...usersToAdd]);
    setFormUsers(getEmptyUser());
  };

  // Handle identity manager response
  useEffect(() => {
    if (!isCheckingIdentity || !userIdentityInfo) return;

    if (userIdentityInfo.status === 200 && userIdentityInfo.user) {
      // Populate form fields with data from identity manager
      const identityUser = userIdentityInfo.user;
      const { pendingToBeAdded } = identityUser;
      if (pendingToBeAdded) {
        // User can be added, proceed with the process
        addUserToList({
          ...formUsers,
          userName: identityUser.userName && identityUser.userName.length > 0 ? identityUser.userName : formUsers.userName,
          userEmail: identityUser.userEmail && identityUser.userEmail.length > 0 ? identityUser.userEmail : formUsers.userEmail,
          isSKF: typeof identityUser.isSKF === 'boolean' ? identityUser.isSKF : formUsers.isSKF,
        });
        addToast({
          message: t('User_found_and_can_be_added'),
          status: 'ok',
        });
      } else {
        // User already exists in database, cannot add again
        addToast({
          message: t('User_already_created_edit_from_users_list'),
          status: 'warning',
        });
      }
    } else if (userIdentityInfo.status === 204) {
      addToast({
        message: t('User_not_found_self_registration_required'),
        status: 'danger',
      });
    } else {
      addToast({
        message: t('Error_checking_user'),
        status: 'danger',
      });
    }
    setIsCheckingIdentity(false);
  }, [userIdentityInfo, isCheckingIdentity]);

  const onClickIsSkf = () => {
    setFormUsers({
      ...formUsers,
      isSKF: !formUsers.isSKF,
    });
  };

  const onChangeUsername = (value: string) => {
    setFormUsers({
      ...formUsers,
      userName: value.trim(),
    });
  };

  const onChangeEmail = (value: string) => {
    setFormUsers({
      ...formUsers,
      userEmail: value.trim(),
    });
  };

  const onChangeGlobalId = (value: string) => {
    setFormUsers({
      ...formUsers,
      globalId: value,
    });
  };

  const _onAddUser = async () => {
    dispatch({ type: UsersActionTypes.CLEAR_USER_IDENTITY });
    if (!isValidUser(formUsers)) return;

    if (usersToAdd.length === 10)
      return addToast({
        message: `${t(
          'You_reach_the_peak_of_users_you_can_add_at_the_same_time'
        )} (10).`,
        status: 'warning',
      });

    // Check user in identity manager before adding
    setIsCheckingIdentity(true);
    try {
      await dispatch(getUserInIdentityManager(formUsers.userEmail));
      // Handle response in useEffect
    } catch (error) {
      console.error('Error checking user in identity manager:', error);
      addToast({
        message: t('User_not_found_self_registration_required'),
        status: 'danger',
      });
      setIsCheckingIdentity(false);
    }
  };

  const isValidUser = (user: User): boolean => {
    const errorList: any = [];
    // username
    const userName = user.userName.toLowerCase().trim();
    if (user.userName.length === 0) errorList.push('Empty_Username');
    if (
      users?.some((_user) => _user.userName.toLowerCase() === userName) ||
      usersToAdd.some((_user) => _user.userName.toLowerCase() === userName)
    )
      errorList.push('Already_Username');
    if (!isAlphaNumericPlus(formUsers.userName))
      errorList.push(t('Username_cannot_contain_non_alphanumeric_characters'));

    // email
    const userEmail = user.userEmail.toLowerCase().trim();
    if (user.userEmail.length === 0) errorList.push('Empty_Email');
    if (user.userEmail.length > 0 && !isValidEmail(user.userEmail))
      errorList.push('Invalid_Email');
    if (
      users?.some((_user) => _user.userEmail.toLowerCase() === userEmail) ||
      usersToAdd.some((_user) => _user.userEmail.toLowerCase() === userEmail)
    )
      errorList.push('Already_Email');

    // globalId
    const globalId = user.globalId?.toLowerCase().trim();
    if (
      user.globalId?.length !== 0 &&
      (users?.some((_user) => _user.globalId?.toLowerCase() === globalId) ||
        usersToAdd.some((_user) => _user.globalId?.toLowerCase() === globalId))
    )
      errorList.push('Already_GlobalId');
    if (formUsers.globalId && !isAlphaNumeric(formUsers.globalId))
      errorList.push(t('Global_Id_cannot_contain_non_alphanumeric_characters'));

    errorList.forEach((_message) =>
      addToast({ status: 'danger', message: t(_message) })
    );

    return errorList.length === 0;
  };

  const onClickCancel = () => {
    setPathname(UserManagementRoute.path);
  };

  const onClickSubmit = () => {
    dispatch(postUsers(usersToAdd));
    setFetching(true);
    setLoading(true);
  };

  const isFormButtonDisabled =
    formUsers.userName.length === 0 || formUsers.userEmail.length === 0;

  return (
    <div>
      <div className={style.form}>
        {/*<div>*/}
        {/*  <div*/}
        {/*    className={style.checkbox}*/}
        {/*  >*/}
        {/*    <Checkbox*/}
        {/*      square*/}
        {/*      isChecked={formData.isEnabled}*/}
        {/*      onClick={onClickIsEnabled}*/}
        {/*    >*/}
        {/*      {t('is_enabled')}*/}
        {/*    </Checkbox>*/}
        {/*  </div>*/}
        {/*</div>*/}
        <div>
          <Input
            placeholder={t('Username')}
            value={formUsers.userName}
            maxLength={128}
            onChange={onChangeUsername}
          />
        </div>
        {/*email*/}
        <div>
          <Input
            placeholder={t('Email')}
            value={formUsers.userEmail}
            onChange={onChangeEmail}
          />
        </div>
        {/*globalId*/}
        <div className={style.isSkf}>
          <div className={style.checkbox}>
            <Checkbox
              square
              leftLabel
              isChecked={formUsers.isSKF}
              onClick={onClickIsSkf}
            >
              {t('Is_SKF')}
            </Checkbox>
          </div>
          <div>
            <Input
              placeholder={t('Global_Id')}
              value={formUsers.globalId}
              onChange={onChangeGlobalId}
            />
          </div>
        </div>
        {/*add*/}
        <ButtonPrimary
          disabled={isFormButtonDisabled || isCheckingIdentity}
          onClick={_onAddUser}
        >
          {isCheckingIdentity ? t('Checking_user') : t('Add_User')}
        </ButtonPrimary>
      </div>
      <UserManagementAddDataTable
        users={usersToAdd}
        onDeleteUser={onDeleteUser}
      />
      <div className={style.navigation}>
        <ButtonPrimary onClick={onClickCancel} outline>
          {t('Cancel')}
        </ButtonPrimary>

        <ButtonPrimary
          disabled={usersToAdd.length === 0}
          onClick={onClickSubmit}
        >
          {t('Save_and_assign_roles')}
        </ButtonPrimary>
      </div>
    </div>
  );
};

export default UserManagementAdd;
